import { useMutation, useQuery } from "convex/react";
import { useState } from "react";
import { api } from "../../convex/_generated/api";
import { Button, Card, Input, PageHeader, Select, Spinner, useRun } from "@/components/ui";

type Role = "admin" | "pm" | "viewer";
const ROLE_HELP: Record<Role, string> = { admin: "Everything, incl. library, team, baseline and approvals", pm: "Edit projects: setup, deliverables, progress, hours", viewer: "Read-only" };

export function Team() {
  const data = useQuery(api.account.members);
  const invite = useMutation(api.account.invite);
  const cancel = useMutation(api.account.cancelInvite);
  const setRole = useMutation(api.account.setRole);
  const remove = useMutation(api.account.removeMember);
  const run = useRun();
  const [email, setEmail] = useState("");
  const [role, setR] = useState<Role>("pm");
  if (!data) return <Spinner />;
  return (
    <div className="mx-auto max-w-4xl space-y-4 p-6">
      <PageHeader title="Team" sub="People in your organisation and their roles." />
      <Card title="Invite someone">
        <form
          className="flex flex-wrap gap-2"
          onSubmit={async (e) => {
            e.preventDefault();
            const ok = await run(() => invite({ email, role }), `Invited ${email}. New users join when they sign up with that email; existing users get a Join banner.`);
            if (ok !== undefined) setEmail("");
          }}
        >
          <Input type="email" placeholder="email@company.com" aria-label="Invite email" value={email} onChange={(e) => setEmail(e.target.value)} className="w-72" required />
          <Select value={role} onChange={(e) => setR(e.target.value as Role)} className="w-40" aria-label="Invite role">
            <option value="admin">Admin</option>
            <option value="pm">Project manager</option>
            <option value="viewer">Viewer</option>
          </Select>
          <Button variant="primary" type="submit">
            Invite
          </Button>
        </form>
        <p className="mt-2 text-xs text-slate-500">{ROLE_HELP[role]}</p>
      </Card>
      <Card title="Members" bodyClass="p-0">
        <table className="w-full text-sm">
          <tbody>
            {data.members.map((m) => (
              <tr key={m._id} className="border-t border-slate-100 first:border-0">
                <td className="px-4 py-2">{m.email}</td>
                <td className="w-48 px-4 py-2">
                  <Select aria-label={`Role for ${m.email}`} value={m.role} onChange={(e) => run(() => setRole({ membershipId: m._id, role: e.target.value as Role }), "Role updated")}>
                    <option value="admin">Admin</option>
                    <option value="pm">Project manager</option>
                    <option value="viewer">Viewer</option>
                  </Select>
                </td>
                <td className="w-24 px-4 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => run(() => remove({ membershipId: m._id }), "Removed")}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
            {data.invites.map((i) => (
              <tr key={i._id} className="border-t border-slate-100 text-slate-500">
                <td className="px-4 py-2">{i.email} (invited)</td>
                <td className="px-4 py-2">{i.role}</td>
                <td className="px-4 py-2 text-right">
                  <Button size="sm" variant="ghost" onClick={() => run(() => cancel({ id: i._id }))}>
                    Cancel
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}
